import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../i18n'
import { actorDisplayName } from '../lib/actor-name'
import type { ActorKind, HistoryKind } from '../lib/types'

export interface Toast {
  id: string
  actor: string
  kind: HistoryKind
  /** Display name of the author and which side of the member they are, carried from the history
   *  entry so the toast attributes an agent edit the way the lock bar does ("Sofia's agent").
   *  Absent on a legacy entry and on locally generated notices. */
  actorName?: string
  actorKind?: ActorKind
  /** Overrides the derived "<actor> edited this artefact" line — for notifications that are
   *  not a modification at all (a copied § reference, a rejected edit inside a locked §). */
  message?: string
  /** What changed — the recorded modification window (for a richer notification). */
  prompt?: string
  before?: string
  after?: string
  contextBefore?: string
  contextAfter?: string
}

const DISMISS_MS = 7000
/** Keep the first n chars (trailing ellipsis). */
const head = (s: string, n: number): string => (s.length > n ? s.slice(0, n) + '…' : s)
/** Keep the last n chars (leading ellipsis) — for the context just before a change. */
const tail = (s: string, n: number): string => (s.length > n ? '…' + s.slice(-n) : s)

/**
 * Transient, accessible notifications shown when a modification lands on the part the
 * user is currently viewing (AI edits and other people's edits). Each toast
 * auto-dismisses; the region is polite so it never steals focus.
 */
export function Toasts({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  // Portal to <body> so `position:fixed` is viewport-relative — otherwise a transformed/zoomed/
  // scrolling ancestor (.canvasWrap, the zoomed .paper) traps it and it scrolls with the page.
  return createPortal(
    <div className="toasts" role="region" aria-live="polite" aria-label="Activity">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body,
  )
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const { t } = useLang()
  const [paused, setPaused] = useState(false)
  useEffect(() => {
    if (paused) return // hovering → keep it open so the change can be read
    const timer = setTimeout(() => onDismiss(toast.id), DISMISS_MS)
    return () => clearTimeout(timer)
  }, [toast.id, onDismiss, paused])

  // An explicit `message` wins (notices are not modifications at all). Otherwise: name the author
  // whenever the entry recorded one — through the same helper as the lock bar and the history
  // table — and fall back to the old wording for entries that carry no display name.
  const message =
    toast.message ??
    (toast.actorName
      ? `${actorDisplayName(toast, toast.actor, t('sec_lock_agent'))} ${t('toast_edited')}`
      : toast.kind === 'ai'
        ? t('toast_ai')
        : `${toast.actor} ${t('toast_edited')}`)
  const hasDiff = Boolean(toast.before || toast.after)

  return (
    <div
      className={`toast ${toast.kind}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <span className="toast-ic" aria-hidden="true">{toast.kind === 'ai' ? '✦' : '✎'}</span>
      <div className="toast-body">
        <span className="toast-msg">{message}</span>
        {toast.prompt && <span className="toast-prompt">“{head(toast.prompt, 120)}”</span>}
        {hasDiff && (
          <span className="toast-diff">
            {toast.contextBefore && <span className="diff-ctx">{tail(toast.contextBefore, 36)}</span>}
            {toast.before && <del className="diff-del">{head(toast.before, 200)}</del>}
            {toast.after && <ins className="diff-ins">{head(toast.after, 320)}</ins>}
            {toast.contextAfter && <span className="diff-ctx">{head(toast.contextAfter, 36)}</span>}
          </span>
        )}
      </div>
      <button className="toast-x" aria-label={t('close')} onClick={() => onDismiss(toast.id)}>✕</button>
    </div>
  )
}
